import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import Spinner from "../components/Spinner";
import {
  PRODUCT_CONDITIONS,
  ALLOWED_CURRENCIES,
  DIMENSION_UNIT,
  WEIGHT_UNIT,
} from "../constants/productAddingConstants";
import { emptyFormForProductRespose, createEmptyVariant } from "./commonForms";
import ImageUploader from "../components/add-product/ImageUploader";

const API_BASE = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const DRAFT_KEY = "add_product_draft";

/* ─────────────────────────────────────────
   Design tokens — single source of truth
───────────────────────────────────────── */
const T = {
  primary: "#1C3FAA",
  primaryHov: "#162E80",
  success: "#0E7C4A",
  danger: "#C0392B",
  border: "#DDE3F0",
  muted: "#6B7A99",
  bg: "#F5F7FC",
  white: "#FFFFFF",
  text: "#0F1C3F",
  textLight: "#6B7A99",
  radius: "10px",
  shadow: "0 2px 12px rgba(15,28,63,0.07)",
  shadowMd: "0 4px 24px rgba(15,28,63,0.11)",
};

/* ─────────────────────────────────────────
   Shared micro-components
───────────────────────────────────────── */
const Required = () => (
  <span style={{ color: T.danger, marginLeft: 2 }}>*</span>
);

const SectionCard = ({ title, subtitle, children, style = {} }) => (
  <div
    style={{
      background: T.white,
      border: `1px solid ${T.border}`,
      borderRadius: T.radius,
      boxShadow: T.shadow,
      marginBottom: 24,
      overflow: "hidden",
      ...style,
    }}
  >
    {title && (
      <div
        style={{
          padding: "16px 24px",
          borderBottom: `1px solid ${T.border}`,
          background: "#FAFBFF",
        }}
      >
        <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: T.text }}>
          {title}
        </p>
        {subtitle && (
          <p style={{ margin: "2px 0 0", fontSize: 12, color: T.textLight }}>
            {subtitle}
          </p>
        )}
      </div>
    )}
    <div style={{ padding: "20px 24px" }}>{children}</div>
  </div>
);

const FormField = ({ label, required, unit, error, children }) => (
  <div style={{ marginBottom: 18 }}>
    <label
      style={{
        display: "block",
        fontSize: 13,
        fontWeight: 600,
        color: T.text,
        marginBottom: 6,
        letterSpacing: "0.01em",
      }}
    >
      {label}
      {required && <Required />}
      {unit && (
        <span style={{ fontWeight: 400, color: T.muted, marginLeft: 4 }}>
          ({unit})
        </span>
      )}
    </label>
    {children}
    {error && (
      <p style={{ margin: "4px 0 0", fontSize: 12, color: T.danger }}>
        {error}
      </p>
    )}
  </div>
);

const inputStyle = (invalid = false) => ({
  width: "100%",
  padding: "9px 13px",
  fontSize: 14,
  color: T.text,
  background: T.white,
  border: `1.5px solid ${invalid ? T.danger : T.border}`,
  borderRadius: 7,
  outline: "none",
  transition: "border-color 0.15s",
  boxSizing: "border-box",
});

/* ─────────────────────────────────────────
   Breadcrumb
───────────────────────────────────────── */
const Breadcrumb = ({ category, subcategory }) => {
  const crumbs = [
    { label: "Add Product", active: !category },
    category && { label: category, active: !subcategory },
    subcategory && { label: subcategory, active: true },
  ].filter(Boolean);

  return (
    <nav style={{ marginBottom: 20 }}>
      <ol
        style={{
          listStyle: "none",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 4,
          margin: 0,
          padding: 0,
        }}
      >
        {crumbs.map((crumb, i) => (
          <li key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {i > 0 && (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke={T.muted}
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
            <span
              style={{
                fontSize: 13,
                fontWeight: crumb.active ? 700 : 500,
                color: crumb.active ? T.primary : T.muted,
                background: crumb.active ? "#EBF0FF" : "transparent",
                padding: crumb.active ? "3px 10px" : "3px 4px",
                borderRadius: 20,
              }}
            >
              {crumb.label}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
};

/* ─────────────────────────────────────────
   Step indicator
───────────────────────────────────────── */
const STEPS = ["Category", "Subcategory", "Product Details"];

const StepIndicator = ({ step }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 32 }}>
    {STEPS.map((label, i) => {
      const s = i + 1;
      const done = step > s;
      const active = step === s;
      return (
        <React.Fragment key={s}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 700,
                background: done ? T.success : active ? T.primary : T.border,
                color: done || active ? T.white : T.muted,
                transition: "all 0.2s",
                boxShadow: active ? `0 0 0 4px #D4DCFF` : "none",
              }}
            >
              {done ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                s
              )}
            </div>
            <span
              style={{
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                color: active ? T.primary : done ? T.success : T.muted,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div
              style={{
                flex: 1,
                height: 2,
                margin: "0 8px",
                marginBottom: 20,
                background: done ? T.success : T.border,
                transition: "background 0.3s",
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/* ─────────────────────────────────────────
   Alert banner
───────────────────────────────────────── */
const Alert = ({ type, message }) => {
  if (!message) return null;
  const isSuccess = type === "success";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "12px 16px",
        borderRadius: 8,
        background: isSuccess ? "#ECFDF5" : "#FEF2F2",
        border: `1px solid ${isSuccess ? "#6EE7B7" : "#FECACA"}`,
        marginBottom: 16,
        fontSize: 14,
        color: isSuccess ? T.success : T.danger,
        fontWeight: 500,
      }}
    >
      <span>{isSuccess ? "✓" : "✕"}</span>
      <span>{message}</span>
    </div>
  );
};

/* ─────────────────────────────────────────
   Main component
───────────────────────────────────────── */
export default function AddProduct() {
  const { auth } = useAuth();
  const token = auth?.token;
  const sellerId = auth?.userId;

  const [attributesMeta, setAttributesMeta] = useState({
    productAttributes: [],
    variantAttributes: [],
  });
  const [productAttributesValues, setProductAttributesValues] = useState({});
  const [variants, setVariants] = useState([createEmptyVariant()]);
  const [form, setForm] = useState(emptyFormForProductRespose);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [touched, setTouched] = useState({});
  const [step, setStep] = useState(1);
  const [productImages, setProductImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  /* ── Derived breadcrumb labels ── */
  const categoryName =
    categories.find((c) => c.id === Number(form.categoryId))?.name || null;
  const subCategoryName =
    subCategories.find((s) => s.id === Number(form.subCategoryId))?.name ||
    null;

  const step3Fields = [
    {
      name: "productName",
      label: "Product Name",
      type: "text",
      required: true,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: true,
    },
    { name: "brand", label: "Brand", type: "text", required: false },
    {
      name: "modelNumber",
      label: "Model Number",
      type: "text",
      required: false,
    },
    {
      name: "productCondition",
      label: "Condition",
      type: "select",
      required: true,
      options: PRODUCT_CONDITIONS,
    },
    {
      name: "currency",
      label: "Currency",
      type: "select",
      required: true,
      options: ALLOWED_CURRENCIES,
    },
    {
      name: "dimensionUnit",
      label: "Dimension Unit",
      type: "select",
      required: true,
      options: DIMENSION_UNIT,
    },
    {
      name: "weightUnit",
      label: "Weight Unit",
      type: "select",
      required: true,
      options: WEIGHT_UNIT,
    },
    {
      name: "returnPolicy",
      label: "Return Policy",
      type: "text",
      required: false,
    },
    {
      name: "warrantyPeriod",
      label: "Warranty Period (months)",
      type: "number",
      required: false,
    },
  ];

  const authHeaders = useCallback(() => {
    if (!token) {
      throw new Error("Authentication token is missing");
    }

    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }, [token]);

  /* ── Categories ── */
  useEffect(() => {
    if (!API_BASE) return;
    fetch(`${API_BASE}/products/categories?page=0&size=100`)
      .then((r) => r.json())
      .then((r) => setCategories(r.content || []))
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load categories." }),
      );
  }, []);

  /* ── Subcategory fetch ── */
  const handleCategoryChange = (e) => {
    const selectedId = Number(e.target.value);
    setForm((p) => ({ ...p, categoryId: selectedId, subCategoryId: null }));
    setSubCategories([]);
    fetch(`${API_BASE}/products/subcategories/${selectedId}`)
      .then((r) => r.json())
      .then((r) => setSubCategories(Array.isArray(r) ? r : r.content || []))
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load subcategories." }),
      );
  };

  const handleSubcategoryChange = (e) => {
    setForm((p) => ({ ...p, subCategoryId: Number(e.target.value) }));
  };

  /* ── Attribute fetch — FIX: added form.categoryId to deps ── */
  useEffect(() => {
    if (!form.subCategoryId || !form.categoryId) return;
    fetch(
      `${API_BASE}/products/subcategories/categories/${form.categoryId}/subcategories/${form.subCategoryId}/attributes`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
      .then((r) => r.json())
      .then((data) => setAttributesMeta(data))
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load attributes." }),
      );
  }, [form.subCategoryId, form.categoryId]); // FIX: categoryId was missing

  /* ── Validation ── */
  const requiredFields = {
    1: ["categoryId"],
    2: ["subCategoryId"],
    3: step3Fields.filter((f) => f.required).map((f) => f.name),
  };

  const isStepValid = (s) => {
    const fields =
      s === 3
        ? step3Fields.filter((f) => f.required).map((f) => f.name)
        : requiredFields[s] || [];

    const baseValid = fields.every((f) => {
      const v = form[f];
      return v !== null && v !== undefined && String(v).trim() !== "";
    });
    if (!baseValid) return false;

    if (s === 3) {
      if (!variants.length) return false;
      return variants.every(
        (v) =>
          v.sku?.trim() &&
          !isNaN(Number(v.price)) &&
          Number(v.price) >= 0 &&
          !isNaN(Number(v.availableQuantity)) &&
          Number(v.availableQuantity) >= 0,
      );
    }
    return true;
  };

  const markStepTouched = (s) => {
    setTouched((prev) => {
      const next = { ...prev };
      (requiredFields[s] || []).forEach((f) => {
        next[f] = true;
      });
      if (s === 3)
        step3Fields.forEach((f) => {
          next[f.name] = true;
        });
      return next;
    });
  };

  /* ── Form change ── */
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    const finalValue =
      type === "checkbox"
        ? checked
        : type === "number"
          ? value === ""
            ? ""
            : Math.max(0, Number(value))
          : value;
    setForm((p) => ({ ...p, [name]: finalValue }));
    setTouched((p) => ({ ...p, [name]: true }));
  };

  /* ── Draft restore ── */
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (!draft) return;
    const parsed = JSON.parse(draft);
    setForm(parsed.form || emptyFormForProductRespose);
    setStep(parsed.step || 1);
    setVariants(parsed.variants || [createEmptyVariant()]);
    setProductAttributesValues(parsed.productAttributesValues || {});
  }, []);

  /* ── Draft auto-save ── */
  useEffect(() => {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ form, step, variants, productAttributesValues }),
    );
  }, [form, step, variants, productAttributesValues]);

  /* ── Image validation ── */
  const validateImages = (files, maxSizeMB = 1) => {
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    const max = maxSizeMB * 1024 * 1024;
    const valid = [],
      errors = [];
    files.forEach((f) => {
      if (!allowed.includes(f.type)) {
        errors.push(`${f.name}: invalid format`);
        return;
      }
      if (f.size > max) {
        errors.push(`${f.name}: exceeds ${maxSizeMB}MB`);
        return;
      }
      valid.push(f);
    });
    return { valid, errors };
  };

  /* ── Image upload ── */
  const uploadImages = async (files, ownerId) => {
    if (!files?.length) return [];

    const fd = new FormData();

    files.forEach((f) => {
      fd.append("imageFiles", f);
    });

    fd.append("ownerId", ownerId);

    const res = await fetch(`${API_BASE}/products/upload/images`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: fd,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data.message || data.error || `Image upload failed (${res.status})`,
      );
    }

    return data;
  };

  /* ── FIX: resetAll now clears ALL state ── */
  const resetAll = () => {
    localStorage.removeItem(DRAFT_KEY);
    setStep(1);
    setForm(emptyFormForProductRespose);
    setTouched({});
    setProductImages([]);
    setVariants([createEmptyVariant()]);
    setProductAttributesValues({});
    setAttributesMeta({ productAttributes: [], variantAttributes: [] });
    setSubCategories([]);
    setStatus({ type: null, message: "" });
  };

  /* ── Attribute mapper ── */
  const mapAttributes = (attrs) => {
    const all = [
      ...attributesMeta.productAttributes,
      ...attributesMeta.variantAttributes,
    ];
    return Object.entries(attrs || {}).map(([code, value]) => {
      const meta = all.find((a) => a.code === code);
      if (!meta) throw new Error(`Attribute metadata not found for ${code}`);
      if (meta.options?.length > 0) {
        const opt = meta.options.find((o) => o.id === Number(value));
        if (!opt) throw new Error(`Invalid value "${value}" for ${code}`);
        return {
          attributeCode: code,
          optionId: opt.id,
          valueText: null,
          valueNumber: null,
          valueBoolean: null,
        };
      }
      const t = meta.type || meta.inputType;
      if (t === "NUMBER")
        return {
          attributeCode: code,
          optionId: null,
          valueText: null,
          valueNumber: value !== "" ? Number(value) : null,
          valueBoolean: null,
        };
      if (t === "BOOLEAN")
        return {
          attributeCode: code,
          optionId: null,
          valueText: null,
          valueNumber: null,
          valueBoolean: Boolean(value),
        };
      return {
        attributeCode: code,
        optionId: null,
        valueText: value ? String(value) : null,
        valueNumber: null,
        valueBoolean: null,
      };
    });
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (!productImages.length) {
      setStatus({
        type: "error",
        message: "At least one product image is required.",
      });
      return;
    }
    if (!isStepValid(3)) {
      markStepTouched(3);
      setStatus({
        type: "error",
        message: "Please complete all required fields.",
      });
      return;
    }

    setSubmitting(true);
    setStatus({ type: null, message: "" });

    try {
      /* FIX: renamed to avoid variable shadowing */
      const uploadedProductImages = await uploadImages(productImages, sellerId);
      const productImagePayload = {
        imageIds: uploadedProductImages.map((i) => i.id),
        mainImageId: uploadedProductImages[0]?.id ?? null,
      };

      const updatedVariants = await Promise.all(
        variants.map(async (v) => {
          const uploaded = await uploadImages(v.images || [], sellerId);
          return { ...v, imageIds: uploaded.map((i) => i.id) };
        }),
      );

      const hasVariants = updatedVariants.length > 0;

      const payload = {
        sellerId,
        productName: form.productName,
        description: form.description,
        brand: form.brand || null,
        modelNumber: form.modelNumber || null,
        dimensionUnit: form.dimensionUnit,
        weightUnit: form.weightUnit,
        returnPolicy: form.returnPolicy,
        warrantyPeriod:
          form.warrantyPeriod !== "" ? String(form.warrantyPeriod) : null,
        costPrice: form.costPrice ? Number(form.costPrice) : null,
        currency: form.currency,
        taxRate: form.taxRate ? Number(form.taxRate) : null,
        productCondition: form.productCondition,
        categoryId: Number(form.categoryId),
        subCategoryId: Number(form.subCategoryId),
        tags: [],
        attributeRequests: mapAttributes(productAttributesValues),
        imageIds: productImagePayload.imageIds,
        mainImageId: productImagePayload.mainImageId,
        sku: !hasVariants ? form.sku : null,
        price: !hasVariants ? Number(form.price) : null,
        availableQuantity: !hasVariants ? Number(form.availableQuantity) : null,
        /* FIX: null instead of 0 for empty dimensions */
        weight: !hasVariants
          ? form.weight
            ? Number(form.weight)
            : null
          : null,
        width: !hasVariants ? (form.width ? Number(form.width) : null) : null,
        length: !hasVariants
          ? form.length
            ? Number(form.length)
            : null
          : null,
        height: !hasVariants
          ? form.height
            ? Number(form.height)
            : null
          : null,
        variantRequests: hasVariants
          ? updatedVariants.map((v, i) => ({
              sku: v.sku,
              price: v.price !== "" ? Number(v.price) : null,
              availableQuantity:
                v.availableQuantity !== "" ? Number(v.availableQuantity) : null,
              /* FIX: null instead of 0 for empty variant dimensions */
              weight: v.weight ? Number(v.weight) : null,
              width: v.width ? Number(v.width) : null,
              height: v.height ? Number(v.height) : null,
              length: v.length ? Number(v.length) : null,
              isDefault: i === 0,
              attributeRequests: mapAttributes(v.attributes),
              imageIds: v.imageIds || [],
            }))
          : [],
      };

      const res = await fetch(`${API_BASE}/products/product/create`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Product creation failed");

      setStatus({ type: "success", message: "Product created successfully." });
      setTimeout(resetAll, 1800);
    } catch (err) {
      setStatus({
        type: "error",
        message: err.message || "Something went wrong.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Variant helper ── */
  const updateVariant = (index, updater) =>
    setVariants((prev) => prev.map((v, i) => (i === index ? updater(v) : v)));

  /* ─────────────────────────────────────────
     UI
  ───────────────────────────────────────── */
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        padding: "32px 16px",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        {/* ── Page header ── */}
        <div style={{ marginBottom: 24 }}>
          <Breadcrumb category={categoryName} subcategory={subCategoryName} />
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 800,
              color: T.text,
              letterSpacing: "-0.5px",
            }}
          >
            Add New Product
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: T.textLight }}>
            Fill in the details below to list your product on the marketplace.
          </p>
        </div>

        {/* ── Outer card ── */}
        <div
          style={{
            background: T.white,
            borderRadius: 14,
            boxShadow: T.shadowMd,
            border: `1px solid ${T.border}`,
            padding: "28px 32px",
          }}
        >
          <StepIndicator step={step} />

          {/* ══════════════════════════════
              STEP 1 — Category
          ══════════════════════════════ */}
          {step === 1 && (
            <div style={{ maxWidth: 480, margin: "0 auto" }}>
              <SectionCard
                title="Select a Category"
                subtitle="Choose the top-level category that best fits your product."
              >
                <FormField
                  label="Category"
                  required
                  error={
                    touched.categoryId && !form.categoryId
                      ? "Please select a category"
                      : ""
                  }
                >
                  <select
                    style={inputStyle(touched.categoryId && !form.categoryId)}
                    name="categoryId"
                    value={form.categoryId || ""}
                    onChange={handleCategoryChange}
                  >
                    <option value="">— Select a category —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </FormField>

                {/* Category cards (visual helper) */}
                {categories.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    {categories.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() =>
                          handleCategoryChange({ target: { value: c.id } })
                        }
                        style={{
                          padding: "6px 14px",
                          fontSize: 13,
                          fontWeight: 500,
                          borderRadius: 20,
                          border: `1.5px solid ${Number(form.categoryId) === c.id ? T.primary : T.border}`,
                          background:
                            Number(form.categoryId) === c.id
                              ? "#EBF0FF"
                              : T.white,
                          color:
                            Number(form.categoryId) === c.id
                              ? T.primary
                              : T.muted,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
              </SectionCard>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  style={btnStyle("primary", !isStepValid(1))}
                  disabled={!isStepValid(1)}
                  onClick={() => {
                    if (!isStepValid(1)) {
                      markStepTouched(1);
                      return;
                    }
                    setStep(2);
                  }}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ══════════════════════════════
              STEP 2 — Subcategory
          ══════════════════════════════ */}
          {step === 2 && (
            <div style={{ maxWidth: 480, margin: "0 auto" }}>
              <SectionCard
                title="Select a Subcategory"
                subtitle={`Subcategories for ${categoryName || "selected category"}`}
              >
                <FormField
                  label="Subcategory"
                  required
                  error={
                    touched.subCategoryId && !form.subCategoryId
                      ? "Please select a subcategory"
                      : ""
                  }
                >
                  <select
                    style={inputStyle(
                      touched.subCategoryId && !form.subCategoryId,
                    )}
                    name="subCategoryId"
                    value={form.subCategoryId || ""}
                    onChange={handleSubcategoryChange}
                  >
                    <option value="">— Select a subcategory —</option>
                    {subCategories.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </FormField>

                {subCategories.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    {subCategories.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() =>
                          handleSubcategoryChange({ target: { value: s.id } })
                        }
                        style={{
                          padding: "6px 14px",
                          fontSize: 13,
                          fontWeight: 500,
                          borderRadius: 20,
                          border: `1.5px solid ${Number(form.subCategoryId) === s.id ? T.primary : T.border}`,
                          background:
                            Number(form.subCategoryId) === s.id
                              ? "#EBF0FF"
                              : T.white,
                          color:
                            Number(form.subCategoryId) === s.id
                              ? T.primary
                              : T.muted,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </SectionCard>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  style={btnStyle("secondary")}
                  onClick={() => setStep(1)}
                >
                  ← Back
                </button>
                <button
                  style={btnStyle("primary", !isStepValid(2))}
                  disabled={!isStepValid(2)}
                  onClick={() => {
                    if (!isStepValid(2)) {
                      markStepTouched(2);
                      return;
                    }
                    setStep(3);
                  }}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ══════════════════════════════
              STEP 3 — Product Details
          ══════════════════════════════ */}
          {step === 3 && (
            <>
              {/* Basic info */}
              <SectionCard
                title="Basic Information"
                subtitle="Core product details visible to buyers."
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0 24px",
                  }}
                >
                  {step3Fields.map((field) => {
                    const invalid =
                      touched[field.name] &&
                      field.required &&
                      (form[field.name] === null ||
                        form[field.name] === undefined ||
                        String(form[field.name]).trim() === "");
                    const fullWidth = field.name === "description";
                    return (
                      <div
                        key={field.name}
                        style={fullWidth ? { gridColumn: "1 / -1" } : {}}
                      >
                        <FormField
                          label={field.label}
                          required={field.required}
                          error={invalid ? "This field is required" : ""}
                        >
                          {field.type === "textarea" ? (
                            <textarea
                              name={field.name}
                              rows={3}
                              value={form[field.name] || ""}
                              onChange={handleChange}
                              style={{
                                ...inputStyle(invalid),
                                resize: "vertical",
                                minHeight: 80,
                              }}
                            />
                          ) : field.type === "select" ? (
                            <select
                              name={field.name}
                              value={form[field.name] || ""}
                              onChange={handleChange}
                              style={inputStyle(invalid)}
                            >
                              {field.options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={field.type}
                              name={field.name}
                              value={form[field.name] || ""}
                              onChange={handleChange}
                              style={inputStyle(invalid)}
                            />
                          )}
                        </FormField>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>

              {/* Product attributes */}
              {attributesMeta.productAttributes.length > 0 && (
                <SectionCard
                  title="Product Attributes"
                  subtitle="Specifications that apply to all variants of this product."
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "0 24px",
                    }}
                  >
                    {attributesMeta.productAttributes.map((attr) => (
                      <FormField
                        key={attr.id}
                        label={attr.name}
                        required={attr.required}
                        unit={attr.unit}
                      >
                        {attr.inputType === "select" ? (
                          <select
                            style={inputStyle()}
                            value={productAttributesValues[attr.code] || ""}
                            onChange={(e) =>
                              setProductAttributesValues((p) => ({
                                ...p,
                                [attr.code]: e.target.value,
                              }))
                            }
                          >
                            <option value="">
                              {attr.placeholder || "Select"}
                            </option>
                            {attr.options.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.value}
                              </option>
                            ))}
                          </select>
                        ) : attr.inputType === "checkbox" ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              paddingTop: 4,
                            }}
                          >
                            <input
                              type="checkbox"
                              style={{
                                width: 16,
                                height: 16,
                                accentColor: T.primary,
                                cursor: "pointer",
                              }}
                              checked={!!productAttributesValues[attr.code]}
                              onChange={(e) =>
                                setProductAttributesValues((p) => ({
                                  ...p,
                                  [attr.code]: e.target.checked,
                                }))
                              }
                            />
                            <span style={{ fontSize: 13, color: T.muted }}>
                              Enabled
                            </span>
                          </div>
                        ) : (
                          <input
                            type={attr.inputType || "text"}
                            style={inputStyle()}
                            placeholder={attr.placeholder || ""}
                            value={productAttributesValues[attr.code] || ""}
                            onChange={(e) =>
                              setProductAttributesValues((p) => ({
                                ...p,
                                [attr.code]: e.target.value,
                              }))
                            }
                          />
                        )}
                      </FormField>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* Product images */}
              <SectionCard
                title="Product Images"
                subtitle="Upload up to 5 images. First image will be used as the main image."
              >
                <ImageUploader
                  images={productImages}
                  setImages={setProductImages}
                  error={imageError}
                  setError={setImageError}
                  maxImages={5}
                  maxSizeMB={1}
                />
              </SectionCard>

              {/* Variants */}
              <SectionCard
                title="Variants"
                subtitle="Each variant is a unique SKU — e.g. different sizes or colours."
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 16,
                  }}
                >
                  <button
                    type="button"
                    style={btnStyle("outline")}
                    onClick={() =>
                      setVariants((p) => [...p, createEmptyVariant()])
                    }
                  >
                    + Add Variant
                  </button>
                </div>

                {variants.map((variant, index) => (
                  <div
                    key={index}
                    style={{
                      border: `1.5px solid ${index === 0 ? T.primary : T.border}`,
                      borderRadius: 10,
                      padding: "16px 20px",
                      marginBottom: 16,
                      background: index === 0 ? "#FAFBFF" : T.white,
                    }}
                  >
                    {/* Variant header */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: T.text,
                          }}
                        >
                          Variant {index + 1}
                        </span>
                        {index === 0 && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "2px 8px",
                              borderRadius: 20,
                              background: T.primary,
                              color: T.white,
                              letterSpacing: "0.03em",
                            }}
                          >
                            DEFAULT
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        disabled={variants.length === 1}
                        onClick={() =>
                          setVariants((p) =>
                            p.length === 1
                              ? p
                              : p.filter((_, i) => i !== index),
                          )
                        }
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "4px 12px",
                          borderRadius: 6,
                          border: `1px solid ${T.danger}`,
                          background: "transparent",
                          color: T.danger,
                          cursor:
                            variants.length === 1 ? "not-allowed" : "pointer",
                          opacity: variants.length === 1 ? 0.4 : 1,
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    {/* Core fields */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "0 16px",
                      }}
                    >
                      {[
                        { field: "sku", placeholder: "SKU", type: "text" },
                        {
                          field: "price",
                          placeholder: "Price",
                          type: "number",
                        },
                        {
                          field: "availableQuantity",
                          placeholder: "Quantity",
                          type: "number",
                        },
                      ].map(({ field, placeholder, type }) => (
                        <FormField
                          key={field}
                          label={placeholder}
                          required={true}
                        >
                          <input
                            type={type}
                            placeholder={placeholder}
                            style={inputStyle()}
                            value={variant[field] || ""}
                            onChange={(e) =>
                              updateVariant(index, (v) => ({
                                ...v,
                                [field]: e.target.value,
                              }))
                            }
                          />
                        </FormField>
                      ))}
                    </div>

                    {/* Variant attributes */}
                    {attributesMeta.variantAttributes.length > 0 && (
                      <div
                        style={{
                          borderTop: `1px solid ${T.border}`,
                          paddingTop: 14,
                          marginTop: 8,
                        }}
                      >
                        <p
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: T.muted,
                            marginBottom: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                          }}
                        >
                          Variant Attributes
                        </p>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr",
                            gap: "0 16px",
                          }}
                        >
                          {attributesMeta.variantAttributes.map((attr) => (
                            <FormField
                              key={attr.id}
                              label={attr.name}
                              unit={attr.unit}
                            >
                              {attr.inputType === "select" ? (
                                <select
                                  style={inputStyle()}
                                  value={variant.attributes?.[attr.code] || ""}
                                  onChange={(e) =>
                                    updateVariant(index, (v) => ({
                                      ...v,
                                      attributes: {
                                        ...v.attributes,
                                        [attr.code]: e.target.value,
                                      },
                                    }))
                                  }
                                >
                                  <option value="">
                                    {attr.placeholder || "Select"}
                                  </option>
                                  {attr.options.map((opt) => (
                                    <option key={opt.id} value={opt.id}>
                                      {opt.value}
                                    </option>
                                  ))}
                                </select>
                              ) : attr.inputType === "checkbox" ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    paddingTop: 4,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    style={{
                                      width: 16,
                                      height: 16,
                                      accentColor: T.primary,
                                    }}
                                    checked={!!variant.attributes?.[attr.code]}
                                    onChange={(e) =>
                                      updateVariant(index, (v) => ({
                                        ...v,
                                        attributes: {
                                          ...v.attributes,
                                          [attr.code]: e.target.checked,
                                        },
                                      }))
                                    }
                                  />
                                </div>
                              ) : (
                                <input
                                  type={attr.inputType || "text"}
                                  style={inputStyle()}
                                  placeholder={attr.placeholder || ""}
                                  value={variant.attributes?.[attr.code] || ""}
                                  onChange={(e) =>
                                    updateVariant(index, (v) => ({
                                      ...v,
                                      attributes: {
                                        ...v.attributes,
                                        [attr.code]: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              )}
                            </FormField>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Variant images */}
                    <div
                      style={{
                        borderTop: `1px solid ${T.border}`,
                        paddingTop: 14,
                        marginTop: 8,
                      }}
                    >
                      <ImageUploader
                        label={`Variant ${index + 1} Images`}
                        images={variant.images || []}
                        maxImages={5}
                        maxSizeMB={1}
                        error={variant.imageError || ""}
                        setError={(msg) =>
                          updateVariant(index, (v) => ({
                            ...v,
                            imageError: msg,
                          }))
                        }
                        setImages={(updater) =>
                          updateVariant(index, (v) => ({
                            ...v,
                            images:
                              typeof updater === "function"
                                ? updater(v.images || [])
                                : updater,
                          }))
                        }
                      />
                    </div>
                  </div>
                ))}
              </SectionCard>

              {/* Status */}
              <Alert type={status.type} message={status.message} />

              {/* Actions */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: 16,
                  borderTop: `1px solid ${T.border}`,
                  position: "sticky",
                  bottom: 0,
                  background: T.white,
                  margin: "0 -32px -28px",
                  padding: "16px 32px",
                  borderRadius: "0 0 14px 14px",
                }}
              >
                <button
                  style={btnStyle("secondary")}
                  onClick={() => setStep(2)}
                >
                  ← Back
                </button>
                <button
                  style={btnStyle("success", submitting || !isStepValid(3))}
                  disabled={submitting || !isStepValid(3)}
                  onClick={handleSubmit}
                >
                  {submitting ? (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Spinner text="Creating" /> Creating...
                    </span>
                  ) : (
                    "✓ Create Product"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Button style helper
───────────────────────────────────────── */
function btnStyle(variant, disabled = false) {
  const base = {
    padding: "9px 22px",
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "all 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
  if (variant === "primary")
    return { ...base, background: T.primary, color: T.white };
  if (variant === "success")
    return { ...base, background: T.success, color: T.white };
  if (variant === "secondary")
    return { ...base, background: T.border, color: T.text };
  if (variant === "outline")
    return {
      ...base,
      background: "transparent",
      color: T.primary,
      border: `1.5px solid ${T.primary}`,
    };
  return base;
}
